import styled from "styled-components"

import { spacing } from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"

import { ParadiseDetailsSection } from "src/components/Paradise/ParadiseDetailsLayout"
import { ParadiseEventTypeFilter } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceDetails/ParadiseDeviceEvents/ParadiseDeviceEventFilters"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { getPointEventType } from "src/data/devices/logic/deviceLogic"
import { useFetchParadiseDeviceEvents } from "src/data/paradise/paradiseDevices/queries/paradiseDeviceQueries"
import { IParadiseDeviceEvent } from "src/data/paradise/paradiseDevices/types/paradiseDeviceQueryTypes"
import { useSearchParams } from "src/router/useSearchParams"
import { Filter } from "src/ui/Filter/Filter"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import { formatUtcDate } from "src/utils/l10n"

const LIMIT = 20

export function ParadiseDeviceEvents({ deviceId }: { deviceId: string }) {
  const { searchParams, setSearchParams } = useSearchParams({
    keys: [
      {
        key: "event_type",
        type: "string",
        list: true,
      },
      {
        key: "events_offset",
        type: "number",
      },
    ],
  })

  const offset = searchParams.events_offset ?? 0
  const selectedEventTypes = searchParams.event_type ?? []

  const fetchDeviceEvents = useFetchParadiseDeviceEvents({
    deviceId,
    filter: {
      offset,
      limit: LIMIT,
      event_types:
        selectedEventTypes.length > 0
          ? selectedEventTypes
              .map((i) => parseInt(i, 10))
              .filter((i) => !isNaN(i))
          : undefined,
    },
    options: {
      keepPreviousData: true,
    },
  })

  const columns: TableColumn<IParadiseDeviceEvent>[] = [
    {
      value: "type",
      label: "Type",
      columnWidth: "min-content",
      renderCell: (event) => (
        <div>
          <div>
            <Badge size="x-small">{getPointEventType(event.type) ?? ""}</Badge>
          </div>
        </div>
      ),
      popoverContent: (
        <ColumnPopoverWrapper>
          <ParadiseEventTypeFilter
            selectedEventTypes={selectedEventTypes}
            setSelectedEventTypes={(eventTypes) => {
              setSearchParams("event_type", eventTypes, {
                replace: true,
              })
            }}
          />
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      value: "recorded_at",
      label: "Recorded at",
      columnWidth: "min-content",
      renderCell: (event) => (
        <div>
          {formatUtcDate({ date: new Date(event.recorded_at).toISOString() })}
        </div>
      ),
    },
    {
      value: "received_at",
      label: "Received at",
      columnWidth: "min-content",
      renderCell: (event) => (
        <div>
          {formatUtcDate({ date: new Date(event.received_at).toISOString() })}
        </div>
      ),
    },
    {
      value: "value",
      label: "Value",
      columnWidth: "min-content",
      renderCell: (event) => <div>{event.value ?? "-"}</div>,
    },
    {
      value: "fw_version",
      label: "FW version",
      columnWidth: "min-content",
      renderCell: (event) => <div>{event.fw_version ?? "-"}</div>,
    },
  ]

  const {
    rows: newRows,
    headerElements,
    templateColumns,
  } = useTableColumns({
    columns,
    data: fetchDeviceEvents.data?.events ?? [],
  })

  return (
    <div>
      <ParadiseDetailsSection
        title="Device events"
        totalCount={fetchDeviceEvents.data?.paging.total_count}
      />

      {selectedEventTypes.length > 0 && (
        <ParadiseDeviceEventsFilters
          selectedEventTypes={selectedEventTypes}
          setSelectedEventTypes={(eventTypes) => {
            setSearchParams("event_type", eventTypes, {
              replace: true,
            })
          }}
        />
      )}

      <ParadiseTable
        header={headerElements}
        rows={newRows}
        templateColumns={templateColumns}
      />

      {!!fetchDeviceEvents.data?.paging.total_count && (
        <ParadisePager
          limit={LIMIT}
          offset={offset}
          setOffset={(offset) => {
            setSearchParams("events_offset", offset)
          }}
          totalCount={fetchDeviceEvents.data?.paging.total_count}
        />
      )}
    </div>
  )
}

function ParadiseDeviceEventsFilters({
  selectedEventTypes,
  setSelectedEventTypes,
}: {
  selectedEventTypes: string[]
  setSelectedEventTypes: (selectedEventTypes: string[]) => void
}) {
  return (
    <FilterWrapper>
      <Filter
        showAddButton={false}
        filters={[
          {
            id: "event_type",
            label: "Event type",
            value: selectedEventTypes.length.toString() ?? "N/A",
            active: !!selectedEventTypes.length,
            alwaysVisible: true,
            render: (
              <FilterContent>
                <ParadiseEventTypeFilter
                  selectedEventTypes={selectedEventTypes}
                  setSelectedEventTypes={setSelectedEventTypes}
                />
              </FilterContent>
            ),
            onRemove: () => {
              setSelectedEventTypes([])
            },
          },
        ]}
      />
    </FilterWrapper>
  )
}

const FilterWrapper = styled.div`
  margin-top: ${spacing.M};
`

const FilterContent = styled.div`
  padding-inline: ${spacing.XS};
`
